import React from "react";
import { App } from "antd";
import {
  useNavigate,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useSelector } from "react-redux";
import {
  AuditorHomeScreen,
  AuditTrackerScreen,
  AuditViewScreen,
  ReportTrackerScreen,
  CreateAuditScreen,
  UploadReportScreen,
  BlueprintTrackerScreen,
  ReportViewerScreen,
  AuditStatsPage,
  AuditImportStatus,
  AuditReportScreen,
} from "../screens";
import {
  NMHomeScreen,
  NMLoginScreen,
  NMPageNotFoundPage,
  NMAccessRestrictedPage,
} from "@neumetric/blueprint-lib/pages";
import { getCurrentUser, isAuditee } from "@neumetric/blueprint-lib/store";

const LOGIN_ENDPOINT = process.env.REACT_APP_LOGIN_ENDPOINT;
const ENVIRNOMENT_ID = process.env.REACT_APP_ENVIRNOMENT_ID;
const ENVIRNOMENT_TITLE = process.env.REACT_APP_ENVIRNOMENT_TITLE;
const ModuleID = "auditor";
const Environment = { id: ENVIRNOMENT_ID, title: ENVIRNOMENT_TITLE };

const LogInWrapper = () => {
  const navigate = useNavigate();
  return (
    <NMLoginScreen
      onNavigate={(path) => navigate(path)}
      moduleId={ModuleID}
      environment={Environment}
    />
  );
};

const NotFoundRedirect = () => <Navigate replace to="/page-not-found" />;

export const useHeaderNavItems = () => {
  const { pathname } = useLocation();
  const currentUser = useSelector(getCurrentUser);

  const getNavItems = () => {
    let links = [];
    let defaultLink = "";
    if (pathname.startsWith("/A") && currentUser) {
      if (isAuditee(currentUser)) {
        links = [{ label: "Audit", link: "/A/T", id: "audit" }];
      } else {
        links = [
          { label: "Stats", link: "/A/S", id: "stats" },
          { label: "Audit", link: "/A/T", id: "audit" },
          { label: "Report", link: "/A/R", id: "report" },
          { label: "Blueprint", link: "/A/B", id: "blueprint" },
        ];
      }
      const current = links.find(({ link }) => pathname.startsWith(link));
      defaultLink = current ? current.id : "audit";
    }
    return { links, defaultLink };
  };
  return {
    navigation: getNavItems(),
  };
};

const MainRouter = () => {
  const context = App.useApp();
  const { navigation } = useHeaderNavItems();
  const currentUser = useSelector(getCurrentUser);
  const isAuditeeUser = currentUser ? isAuditee(currentUser) : false;
  return (
    <NMHomeScreen
      appContext={context}
      navigation={navigation}
      environment={Environment}
      loginEndpoint={LOGIN_ENDPOINT}
      moduleId={ModuleID}
    >
      <AuditorHomeScreen>
        <Routes>
          <Route index element={<Navigate replace to="A/T" />} />
          <Route path="A/" element={<Navigate replace to="A/T" />} />
          <Route path="A/T" element={<AuditTrackerScreen />} />
          {!isAuditeeUser && (
            <>
              <Route path="A/T/new" element={<CreateAuditScreen />} />
              <Route path="A/T/new/:auditId" element={<CreateAuditScreen />} />
            </>
          )}
          <Route path="A/T/:asmtId" element={<AuditViewScreen />} />
          <Route
            path="A/T/:asmtId/import-status"
            element={<AuditImportStatus />}
          />
          <Route path="A/T/:asmtId/report" element={<AuditReportScreen />} />
          {!isAuditeeUser && (
            <>
              <Route path="A/R" element={<ReportTrackerScreen />} />
              <Route path="A/R/new" element={<UploadReportScreen />} />
              <Route path="A/R/:reportId" element={<ReportViewerScreen />} />
              <Route path="A/S" element={<AuditStatsPage />} />
              <Route path="A/B" element={<BlueprintTrackerScreen />} />
            </>
          )}
          <Route path="*" element={<NotFoundRedirect />} />
        </Routes>
      </AuditorHomeScreen>
    </NMHomeScreen>
  );
};

function AppRouter() {
  const navigate = useNavigate();
  return (
    <Routes>
      <Route path="/login" element={<LogInWrapper />} />
      <Route
        path="/page-not-found"
        element={
          <NMPageNotFoundPage
            loginEndpoint={LOGIN_ENDPOINT}
            onNavigate={(path) => navigate(path)}
          />
        }
      />
      <Route
        path="/restricted"
        element={
          <NMAccessRestrictedPage
            loginEndpoint={LOGIN_ENDPOINT}
            onNavigate={(path) => navigate(path)}
          />
        }
      />
      <Route path="/*" element={<MainRouter />} />
    </Routes>
  );
}

export default AppRouter;
